import React, { useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  BookOnline,
  People,
  Inventory,
  Hotel,
  TrendingUp,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDashboardStats } from '../../store/slices/dashboardSlice';
import { fetchBookings } from '../../store/slices/bookingSlice';
import { fetchPackages } from '../../store/slices/packageSlice';

const StatCard = ({ title, value, icon, color }) => (
  <Paper sx={{ p: 3, textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      {icon}
    </Box>
    <Typography variant="h4" color={color} gutterBottom>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {title}
    </Typography>
  </Paper>
);

const DashboardPage = () => {
  const dispatch = useDispatch();
  const { statistics, loading } = useSelector((state) => state.dashboard);
  const { bookings = [] } = useSelector((state) => state.bookings);
  const { packages = [] } = useSelector((state) => state.packages);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchBookings({ status: 'Confirmed', limit: 50 }));
    dispatch(fetchPackages());
  }, [dispatch]);

  const ongoingByCategory = ['Umrah', 'Ziyarat', 'International', 'Domestic'].map((label) => ({
    label,
    items: (bookings || []).filter((b) => {
      const name = `${b.packageName || ''} ${b.customizedData?.productLine || ''}`.toLowerCase();
      if (label === 'Umrah') return name.includes('umrah');
      if (label === 'Ziyarat') return name.includes('ziyarat');
      if (label === 'Domestic') return name.includes('domestic') || name.includes('pakistan');
      return !name.includes('umrah') && !name.includes('ziyarat') && !name.includes('domestic');
    }).slice(0, 5),
  }));

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const activePackagesCount =
    packages.length > 0
      ? packages.filter((p) => p.status === 'Active' || !p.status).length
      : (statistics?.activePackages || 0);

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
        Rehman Travels — Trips, Umrah & Packages Platform
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bookings (Today)"
            value={statistics?.totalBookingsToday || 0}
            icon={<BookOnline color="primary" sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bookings (This Month)"
            value={statistics?.totalBookingsThisMonth || 0}
            icon={<BookOnline color="secondary" sx={{ fontSize: 40 }} />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue (This Month)"
            value={`$${statistics?.totalRevenueThisMonth || 0}`}
            icon={<TrendingUp color="success" sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={statistics?.totalUsers || 0}
            icon={<People color="info" sx={{ fontSize: 40 }} />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Packages"
            value={activePackagesCount}
            icon={<Inventory color="warning" sx={{ fontSize: 40 }} />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Hotels"
            value={statistics?.activeHotels || 0}
            icon={<Hotel color="error" sx={{ fontSize: 40 }} />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Bookings"
            value={statistics?.pendingBookings || 0}
            icon={<BookOnline color="warning" sx={{ fontSize: 40 }} />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Confirmed Bookings"
            value={statistics?.confirmedBookings || 0}
            icon={<BookOnline color="success" sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
      </Grid>

      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Ongoing trips (confirmed)
      </Typography>
      <Grid container spacing={2}>
        {ongoingByCategory.map((section) => (
          <Grid item xs={12} md={6} key={section.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {section.label}
              </Typography>
              {section.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No bookings in this category.</Typography>
              ) : (
                section.items.map((b) => (
                  <Typography key={b.id} variant="body2" sx={{ py: 0.5 }}>
                    {b.bookingReference} — {b.packageName || 'Custom trip'}
                  </Typography>
                ))
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default DashboardPage;

